import { Alert, Button, Empty, List, Popconfirm, Space, Spin, Table, Tooltip, Typography } from 'antd'
import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { MdDeleteOutline, MdOutlineAdd } from 'react-icons/md'

import { useNotification } from '@/Core/Hooks/useNotification'
import { OpenResourceCardTask } from '@/Core/Tasks/Resources/OpenResourceCardTask'
import { classNames } from '@/Ship/Helpers/classNames'
import { formatPhone } from '@/Ship/Helpers/formatPhone'

import { crmOwnerOf } from '../Owner/CrmOwnerFormContext'
import { CRM_CONTACT_DEFAULT_COLUMNS } from './CrmContact'
import { CrmContactsTabStore } from './CrmContactsTabStore'
import styles from './CrmContactsTab.module.css'

import type { CrmOwnerFormContextLike } from '../Owner/CrmOwnerFormContext'
import type { CrmContactColumn, CrmContactRow } from './CrmContact'
import type { TableColumnsType } from 'antd'

const { Text } = Typography

export interface CrmContactsTabProps {
    /** Ключ ресурсу контакту: `Crm.ClientContact` */
    resource: string
    /** Колекція контактів: `/services/client_contacts` */
    restUrl: string
    /** Поле власника в контакті: `client`, `carrier` */
    ownerField: string
    /** `formContext` картки власника — звідти `uuid`, `@id` і `_permissions.update` */
    ownerFormContext: CrmOwnerFormContextLike | undefined
    /** Колонки; за замовчуванням — «Ім'я», «Прізвище», «Пошта» */
    columns?: CrmContactColumn[]
    /**
     * `full` — вкладка «Контакти» (таблиця), `compact` — блок у вузькій
     * правій колонці «Загального» (`SPEC-009` §8.3, `SPEC-010` §9.4): ті самі
     * дії, але список карток замість таблиці — ПІБ і посада, під ними пошта й
     * телефон, — мала кнопка «Додати» й коротка заглушка порожнього списку.
     * Таблиця з трьома колонками у вузькій колонці не вміщалася
     */
    mode?: 'full' | 'compact'
}

/**
 * Вкладка «Контакти» картки власника CRM (`SPEC-008` §7.1, `SPEC-009` §8.4)
 * і компактний блок «Контакти» на «Загальному» (`mode="compact"`).
 *
 * ⚠️ Власна таблиця, а не ядровий `ResourceList` ресурсу контактів: вкладений
 * список **чужого** ресурсу в картці підхоплює її контекст, і збереження
 * контакту пішло б на адресу клієнта (`.claude/docs/tasks/CORE-ISSUES.md` §3).
 * Картку контакту відкриває `OpenResourceCardTask` — поза контекстом
 * власника, другою шухлядою поверх його картки.
 *
 *  - **«Додати контакт»** — за `_permissions.update` власника; власник у
 *    новому контакті вже стоїть (`initialValues`), вибору власника у формі
 *    немає. З інших місць контакт не створюють (`CREATED_FROM_PARENT`);
 *  - **рядок** відкриває картку контакту, «Видалити» — за `_permissions`
 *    рядка й лише через `Popconfirm`;
 *  - список перечитується після закриття картки — зміни могли бути внесені
 *    не лише кнопкою «Зберегти».
 */
/** «Прізвище Ім'я» — як ПІБ співробітників; без обох — назва з бека. */
const contactName = (row: CrmContactRow): string => {
    const name = [row.lastName, row.name].filter((part) => typeof part === 'string' && part.trim() !== '').join(' ')

    return name !== '' ? name : typeof row.title === 'string' && row.title !== '' ? row.title : '—'
}

/** Пошта й телефон контакту — кожне своїм рядком, що з них є. */
const contactDetails = (row: CrmContactRow): { email: string; phone: string } => ({
    email: typeof row.email === 'string' ? row.email.trim() : '',
    phone: typeof row.phone === 'string' && row.phone !== '' ? formatPhone(row.phone) : '',
})

export const CrmContactsTab = observer(
    ({
        resource,
        restUrl,
        ownerField,
        ownerFormContext,
        columns = CRM_CONTACT_DEFAULT_COLUMNS,
        mode = 'full',
    }: CrmContactsTabProps) => {
        const [store] = useState(() => new CrmContactsTabStore(restUrl, ownerField))
        const { showError } = useNotification()

        const owner = crmOwnerOf(ownerFormContext)
        const ownerUuid = owner?.uuid
        const canAdd = ownerFormContext?.values?._permissions?.update === true

        useEffect(() => {
            void store.load(ownerUuid)
        }, [store, ownerUuid])

        if (!owner) return null

        const reload = () => store.load(owner.uuid)

        const openCard = (mode: 'create' | 'view', contactUuid?: string): void => {
            OpenResourceCardTask({
                resource,
                mode,
                displayMode: 'drawer',
                ...(contactUuid ? { primaryFieldValue: contactUuid } : {}),
                ...(mode === 'create' ? { initialValues: { [ownerField]: owner } } : {}),
                onSave: reload,
                onClose: () => {
                    void reload()
                },
            })
        }

        const remove = (row: CrmContactRow): void => {
            void store.remove(row.uuid, owner.uuid).then((error) => {
                if (error !== null) showError('Помилка', error)
            })
        }

        const removeButton = (row: CrmContactRow) =>
            row._permissions?.delete === true ? (
                // Клік по кнопці видалення не відкриває картку рядка
                <span onClick={(event) => event.stopPropagation()}>
                    <Popconfirm
                        title="Видалити контакт?"
                        description="Контакт зникне зі списку й карток."
                        okText="Видалити"
                        cancelText="Скасувати"
                        okButtonProps={{ danger: true }}
                        onConfirm={() => remove(row)}
                    >
                        <Tooltip title="Видалити">
                            <Button
                                type="text"
                                danger
                                icon={<MdDeleteOutline />}
                                aria-label="Видалити контакт"
                                loading={store.removingUuid === row.uuid}
                            />
                        </Tooltip>
                    </Popconfirm>
                </span>
            ) : null

        const tableColumns: TableColumnsType<CrmContactRow> = [
            ...columns.map((column) => ({
                title: column.title,
                key: column.key,
                dataIndex: column.key,
                render: (value: unknown) =>
                    typeof value === 'string' && value !== '' ? value : <Text type="secondary">—</Text>,
            })),
            {
                title: '',
                key: 'actions',
                width: 56,
                render: (_: unknown, row: CrmContactRow) => removeButton(row),
            },
        ]

        const hidden = store.total - store.items.length
        const compact = mode === 'compact'

        return (
            <Space direction="vertical" style={{ width: '100%' }} size={compact ? 'small' : 'middle'}>
                {canAdd ? (
                    <Button
                        type="primary"
                        icon={<MdOutlineAdd />}
                        onClick={() => openCard('create')}
                        {...(compact ? { size: 'small' as const } : {})}
                    >
                        {compact ? 'Додати' : 'Додати контакт'}
                    </Button>
                ) : null}

                {store.failed ? <Alert type="error" showIcon title="Не вдалося завантажити контакти." /> : null}

                {store.isLoading && store.items.length === 0 ? (
                    <Spin style={{ display: 'block', margin: compact ? '12px auto' : '40px auto' }} />
                ) : store.items.length === 0 && !store.failed ? (
                    <Empty
                        description="Контактів ще немає."
                        {...(compact ? { image: Empty.PRESENTED_IMAGE_SIMPLE } : {})}
                    />
                ) : compact ? (
                    <List<CrmContactRow>
                        size="small"
                        rowKey="uuid"
                        dataSource={store.items}
                        loading={store.isLoading}
                        renderItem={(row) => {
                            const details = contactDetails(row)
                            const position = typeof row.position === 'string' ? row.position.trim() : ''

                            return (
                                <List.Item
                                    className={classNames(styles['item'])}
                                    onClick={() => openCard('view', row.uuid)}
                                    actions={[removeButton(row)]}
                                >
                                    <List.Item.Meta
                                        title={
                                            <span className={classNames(styles['name'])}>
                                                {contactName(row)}
                                                {position !== '' ? (
                                                    <Text type="secondary" className={classNames(styles['position'])}>
                                                        {position}
                                                    </Text>
                                                ) : null}
                                            </span>
                                        }
                                        description={
                                            details.email !== '' || details.phone !== '' ? (
                                                <span className={classNames(styles['details'])}>
                                                    {details.email !== '' ? (
                                                        <span className={classNames(styles['email'])}>{details.email}</span>
                                                    ) : null}
                                                    {details.phone !== '' ? (
                                                        <span className={classNames(styles['phone'])}>{details.phone}</span>
                                                    ) : null}
                                                </span>
                                            ) : (
                                                <Text type="secondary">—</Text>
                                            )
                                        }
                                    />
                                </List.Item>
                            )
                        }}
                    />
                ) : (
                    <Table<CrmContactRow>
                        size="small"
                        rowKey="uuid"
                        pagination={false}
                        columns={tableColumns}
                        dataSource={store.items}
                        loading={store.isLoading}
                        onRow={(row) => ({
                            style: { cursor: 'pointer' },
                            onClick: () => openCard('view', row.uuid),
                        })}
                    />
                )}

                {hidden > 0 ? (
                    <Text type="secondary">
                        Показано {store.items.length} контактів із {store.total}
                    </Text>
                ) : null}
            </Space>
        )
    }
)
